// ============================================================
// useStaging — ステージングモード
//
// backend にリクエストせず、移動/入替/リサイズの変更をローカル Map に溜めて
// 「適用」で一括保存するモード。オーバーレイの配置を試行錯誤する用途と、
// AI最適化（aiOptimize.ts）の提案を確認してから反映する受け皿を兼ねる。
//
// 設計上の要点:
//   - 既存の楽観更新は reservationSyncService.cachedReservations（共有
//     シングルトン）を直接書き換えるが、ステージング中の未保存変更を
//     そこに書くと次の同期（Pusher / 30秒ポーリング）でサーバー正本に
//     上書きされて消える。そのためキャッシュには触れず、GridV2 が表示
//     直前に mergeStagedIntoReservations() でマージする。
//   - 適用は既存の saveScheduleChange（fresh-fetch + メモ永続化 +
//     schedule-only フォールバック）を 5 件バッチで呼ぶ。
//   - 元の位置に戻す操作をステージすると entry は自動削除される
//     （original と一致 → 変更なし扱い）。
// ============================================================

import { useState, useCallback, useMemo } from 'react';
import { Reservation, Vehicle } from './types';
import { saveScheduleChange } from './saveScheduleChange';
import { buildMemoEntry } from './memoLog';
// @ts-ignore
import { reservationSyncService } from '../../services/reservationSyncService';
import { vehicleSyncService } from '../../services/vehicleSyncService';

export interface StagedScheduleValues {
  vehicle_id: string;
  start_date: string;
  end_date: string;
}

export interface StagedChange {
  id: string;
  /** 初回ステージ時の値（差分表示・no-op 判定・メモ用） */
  original: StagedScheduleValues;
  /** 最新のステージ値 */
  current: StagedScheduleValues;
  /** 保存ペイロードの土台になる予約スナップショット */
  reservation: Reservation;
  /** 変更の由来（メモのアクションラベル: 予約移動 / 予約入替 / AI最適化 等） */
  source: string;
}

/** DnD 系フック（useMoveSwap / useReservationSave）に渡す薄いインターフェース */
export interface StagingController {
  active: boolean;
  stage: (r: Reservation, updates: Partial<StagedScheduleValues>, source: string) => void;
}

interface UseStagingProps {
  /** メモの車両コード解決用（filteredVehicles） */
  vehicles: Vehicle[];
  showToast: (message: string, severity?: 'success' | 'error') => void;
}

function sameValues(a: StagedScheduleValues, b: StagedScheduleValues): boolean {
  return (
    a.vehicle_id === b.vehicle_id &&
    a.start_date === b.start_date &&
    a.end_date === b.end_date
  );
}

function formatDateShort(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function useStaging({ vehicles, showToast }: UseStagingProps) {
  const [stagingMode, setStagingMode] = useState(false);
  const [staged, setStaged] = useState<Map<string, StagedChange>>(new Map());
  const [isApplying, setIsApplying] = useState(false);

  /** 変更をステージに積む（既存 entry があれば current を更新、original は初回値を維持） */
  const stage = useCallback(
    (r: Reservation, updates: Partial<StagedScheduleValues>, source: string) => {
      setStaged(prev => {
        const next = new Map(prev);
        const id = String(r.id);
        const existing = next.get(id);
        const original: StagedScheduleValues = existing?.original ?? {
          vehicle_id: String(r.vehicle_id),
          start_date: r.start_date,
          end_date: r.end_date,
        };
        const base = existing?.current ?? original;
        const current: StagedScheduleValues = {
          vehicle_id: updates.vehicle_id != null ? String(updates.vehicle_id) : base.vehicle_id,
          start_date: updates.start_date ?? base.start_date,
          end_date: updates.end_date ?? base.end_date,
        };
        // 元の値に戻った → ステージから除去（変更なし扱い）
        if (sameValues(current, original)) {
          next.delete(id);
          return next;
        }
        next.set(id, {
          id,
          original,
          current,
          reservation: existing?.reservation ?? r,
          source,
        });
        return next;
      });
    },
    [],
  );

  const enterStaging = useCallback(() => {
    setStagingMode(true);
  }, []);

  /** 溜めた変更をすべて破棄（ステージングモードは維持） */
  const clearStaged = useCallback(() => {
    setStaged(new Map());
  }, []);

  /** 変更を破棄してステージングモードを終了 */
  const discardStaging = useCallback(() => {
    setStaged(new Map());
    setStagingMode(false);
  }, []);

  /** メモに記録する変更詳細（車両コード / 日時の差分） */
  const buildChangeDetail = useCallback(
    (c: StagedChange): string => {
      const lines: string[] = [];
      if (c.original.vehicle_id !== c.current.vehicle_id) {
        const fromV = vehicles.find(v => String(v.id) === c.original.vehicle_id);
        const toV = vehicles.find(v => String(v.id) === c.current.vehicle_id);
        lines.push(`${fromV?.vehicle_code || c.original.vehicle_id} → ${toV?.vehicle_code || c.current.vehicle_id}`);
      }
      if (c.original.start_date !== c.current.start_date) {
        lines.push(`貸出: ${formatDateShort(c.original.start_date)} → ${formatDateShort(c.current.start_date)}`);
      }
      if (c.original.end_date !== c.current.end_date) {
        lines.push(`返却: ${formatDateShort(c.original.end_date)} → ${formatDateShort(c.current.end_date)}`);
      }
      return lines.join('\n');
    },
    [vehicles],
  );

  /** 溜めた変更を一括保存 → forceSync → ステージング終了（失敗分はステージに残す） */
  const applyStaging = useCallback(async () => {
    if (isApplying || staged.size === 0) return;
    const changes = Array.from(staged.values());
    setIsApplying(true);
    showToast(`${changes.length}件の変更を適用中...`);

    const failed: StagedChange[] = [];
    let done = 0;
    // メモ永続化で1件あたり fetch+save の2リクエストになるため 5 件バッチ
    // （executeVehicleSwap と同じパターン）
    const BATCH_SIZE = 5;
    for (let i = 0; i < changes.length; i += BATCH_SIZE) {
      const batch = changes.slice(i, i + BATCH_SIZE);
      await Promise.all(
        batch.map(async c => {
          try {
            const updated = {
              ...c.reservation,
              vehicle_id: c.current.vehicle_id,
              start_date: c.current.start_date,
              end_date: c.current.end_date,
            } as Reservation;
            const memoEntry = buildMemoEntry(c.source, buildChangeDetail(c));
            await saveScheduleChange(updated, memoEntry);
            done++;
          } catch (e) {
            console.error('[useStaging] apply failed for reservation', c.id, e);
            failed.push(c);
          }
        }),
      );
    }

    // サーバ正本に揃え直してからステージをクリアする（クリアが先だと
    // 同期完了までの間、保存済みの変更が画面上で一瞬巻き戻って見える）。
    // Pusher 無効 + ポーリングのインスタンスでも確実に反映するため必須。
    try {
      await Promise.all([
        reservationSyncService.forceSync(),
        vehicleSyncService.forceSync(),
      ]);
    } catch {
      /* 同期失敗は次のポーリングに任せる（保存自体は完了している） */
    }

    setStaged(() => {
      const next = new Map<string, StagedChange>();
      for (const f of failed) next.set(f.id, f);
      return next;
    });
    setIsApplying(false);

    if (failed.length === 0) {
      setStagingMode(false);
      showToast(`${done}件の変更を適用しました`);
    } else {
      showToast(`${done}件適用 / ${failed.length}件失敗（失敗分はステージに残っています）`, 'error');
    }
  }, [isApplying, staged, showToast, buildChangeDetail]);

  /** GridCanvas のハイライト用 ID セット */
  const stagedIds = useMemo(() => new Set(Array.from(staged.keys())), [staged]);

  /** DnD 系フックに渡すコントローラ */
  const controller: StagingController = useMemo(
    () => ({ active: stagingMode, stage }),
    [stagingMode, stage],
  );

  /** 表示用: ステージ値を予約配列にマージ（共有キャッシュには触れない） */
  const mergeStagedIntoReservations = useCallback(
    (list: Reservation[]): Reservation[] => {
      if (staged.size === 0) return list;
      return list.map(r => {
        const c = staged.get(String(r.id));
        return c
          ? {
              ...r,
              vehicle_id: c.current.vehicle_id,
              start_date: c.current.start_date,
              end_date: c.current.end_date,
            }
          : r;
      });
    },
    [staged],
  );

  return {
    stagingMode,
    staged,
    stagedIds,
    isApplying,
    stage,
    enterStaging,
    clearStaged,
    discardStaging,
    applyStaging,
    controller,
    mergeStagedIntoReservations,
  };
}
